"use client";

/*
IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.
*/

import { FormEvent, useState } from "react";
import Script from "next/script";
import { HoneypotField } from "@/components/HoneypotField";
import { HONEYPOT_FIELD } from "@/lib/honeypot";
import {
  RECAPTCHA_ACTION,
  RECAPTCHA_FIELD,
  mintRecaptchaToken,
  recaptchaScriptUrl,
} from "@/lib/recaptcha-client";

type SubmitStatus = "idle" | "success" | "error";

interface SubmitState {
  status: SubmitStatus;
  message: string;
}

const DEFAULT_STATE: SubmitState = {
  status: "idle",
  message: "",
};

/**
 * @param recaptchaSiteKey the public reCAPTCHA v3 key, or "" when the control
 * is not configured. Read on the server and passed down, so the key stays a
 * plain server-side variable rather than needing a `NEXT_PUBLIC_` twin inlined
 * at build time — the same arrangement the Application intake uses. Empty
 * turns scoring off on both halves without breaking the form.
 */
export function JoinTeamForm({
  recaptchaSiteKey = "",
}: {
  recaptchaSiteKey?: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>(DEFAULT_STATE);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload: Record<string, string> = {
      applicantName: String(formData.get("applicantName") ?? "").trim(),
      email: String(formData.get("email") ?? "").trim(),
      preferredPosition: String(formData.get("preferredPosition") ?? "").trim(),
      linkedinProfile: String(formData.get("linkedinProfile") ?? "").trim(),
      comments: String(formData.get("comments") ?? "").trim(),
      [HONEYPOT_FIELD]: String(formData.get(HONEYPOT_FIELD) ?? "").trim(),
    };

    setIsSubmitting(true);
    setSubmitState(DEFAULT_STATE);

    try {
      // Minted at submit rather than on mount: a v3 token expires after about
      // two minutes, and someone filling this in slowly would otherwise send a
      // stale one. The shared action and field constants are reused, so this
      // form and the Application intake present one shape to Google.
      //
      // Omitted entirely when reCAPTCHA is not configured, and when Google's
      // script returns nothing; the server treats an absent token as an
      // unavailable control and keeps the honeypot. v3 is invisible — the
      // person filling this in never sees a challenge.
      if (recaptchaSiteKey) {
        const token = await mintRecaptchaToken(
          recaptchaSiteKey,
          RECAPTCHA_ACTION
        );
        if (token) payload[RECAPTCHA_FIELD] = token;
      }

      const response = await fetch("/api/contact/join-team", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = (await response.json().catch(() => null)) as {
        message?: string;
      } | null;

      if (!response.ok) {
        setSubmitState({
          status: "error",
          message:
            result?.message ??
            "We could not submit your application right now. Please try again.",
        });
        return;
      }

      form.reset();
      setSubmitState({
        status: "success",
        message:
          result?.message ??
          "Thanks for applying. We received your submission.",
      });
    } catch {
      setSubmitState({
        status: "error",
        message: "Network error while submitting. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      {/* next/script rather than an appendChild in an effect: it dedupes by
          src and survives client-side navigation. The badge Google renders is
          deliberately NOT hidden — it carries the privacy disclosure their
          terms require. */}
      {recaptchaSiteKey && (
        <Script
          src={recaptchaScriptUrl(recaptchaSiteKey)}
          strategy="afterInteractive"
        />
      )}
      <form
        className="brutal-border bg-white p-6 md:p-8 grid gap-5"
        onSubmit={handleSubmit}
        noValidate
      >
        <div>
          <label
            htmlFor="applicantName"
            className="block text-xs uppercase tracking-widest text-stone-500 mb-2"
          >
            Applicant Name
          </label>
          <input
            id="applicantName"
            name="applicantName"
            type="text"
            required
            className="w-full border-[2px] border-stone-300 px-4 py-3"
            autoComplete="name"
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-xs uppercase tracking-widest text-stone-500 mb-2"
          >
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            className="w-full border-[2px] border-stone-300 px-4 py-3"
            autoComplete="email"
          />
        </div>

        <div>
          <label
            htmlFor="preferredPosition"
            className="block text-xs uppercase tracking-widest text-stone-500 mb-2"
          >
            Preferred Position
          </label>
          <input
            id="preferredPosition"
            name="preferredPosition"
            type="text"
            required
            className="w-full border-[2px] border-stone-300 px-4 py-3"
            autoComplete="organization-title"
          />
        </div>

        <div>
          <label
            htmlFor="linkedinProfile"
            className="block text-xs uppercase tracking-widest text-stone-500 mb-2"
          >
            LinkedIn Profile
          </label>
          <input
            id="linkedinProfile"
            name="linkedinProfile"
            type="url"
            required
            className="w-full border-[2px] border-stone-300 px-4 py-3"
            placeholder="https://www.linkedin.com/in/your-profile"
          />
        </div>

        <div>
          <label
            htmlFor="comments"
            className="block text-xs uppercase tracking-widest text-stone-500 mb-2"
          >
            Comments
          </label>
          <textarea
            id="comments"
            name="comments"
            rows={4}
            required
            className="w-full border-[2px] border-stone-300 px-4 py-3"
          />
        </div>

        <HoneypotField idPrefix="join-team" />

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="brutal-border bg-black text-white px-8 py-3 uppercase tracking-widest text-sm hover:bg-stone-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>

        {submitState.status !== "idle" ? (
          <p
            role="status"
            aria-live="polite"
            className={
              submitState.status === "success"
                ? "text-green-700 text-sm"
                : "text-red-700 text-sm"
            }
          >
            {submitState.message}
          </p>
        ) : null}
      </form>
    </>
  );
}
